import { forwardRef, useId, useState } from 'react';
import cx from 'classnames';
import './Input.css';

/**
 * QHS Input v2
 * ------------
 * Props:
 *   label        — visible label above input (optional)
 *   helper       — small helper text below
 *   error        — string (shown in red, also marks input as error)
 *   success      — string (shown in green)
 *   required     — adds * to label
 *   startIcon    — ReactNode, rendered inside input at start
 *   endIcon      — ReactNode, rendered inside input at end
 *   passwordToggle — when true on a `type="password"` input, renders a
 *                    built-in eye button at the end that flips the input
 *                    between password and text. Mutually exclusive with
 *                    `endIcon`.
 *   mono         — monospace numerals (for MRN, dates, codes)
 *   floatingLabel— use floating-label style (for inline table filters)
 *   fullWidth    — stretch to container
 *   highlighted  — show brand-coloured border when not focused (use
 *                  for filters or fields that should signal "this is
 *                  active / has a value worth noting" to the operator)
 *   size         — 'sm' | 'md' (default) | 'lg'
 */
const EyeIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
    <line x1='1' y1='1' x2='23' y2='23' />
  </svg>
);

const Input = forwardRef(function Input(
  {
    label,
    helper,
    error,
    success,
    required,
    startIcon,
    endIcon,
    passwordToggle = false,
    mono = false,
    floatingLabel = false,
    fullWidth = false,
    highlighted = false,
    size = 'md',
    type = 'text',
    className,
    id,
    value,
    defaultValue,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const [shown, setShown] = useState(false);
  const hasValue = value != null && value !== '' || defaultValue != null && defaultValue !== '';

  // When passwordToggle is enabled, the type flips between
  // password/text on click. Ignore the prop on non-password fields so
  // it can't be misused to expose a sensitive numeric input.
  const showToggle = passwordToggle && type === 'password';
  const effectiveType = showToggle && shown ? 'text' : type;
  const hasEndAdornment = !!endIcon || showToggle;

  const wrapperClasses = cx(
    'qhs-field',
    `qhs-field--${size}`,
    fullWidth && 'qhs-field--full',
    error && 'qhs-field--error',
    success && 'qhs-field--success',
    floatingLabel && 'qhs-field--floating',
    hasValue && 'qhs-field--has-value',
    highlighted && 'qhs-field--highlighted',
    className
  );

  const inputClasses = cx(
    'qhs-field__input',
    mono && 'qhs-mono',
    startIcon && 'qhs-field__input--has-start',
    hasEndAdornment && 'qhs-field__input--has-end'
  );

  const message = error || success || helper;
  const messageType = error ? 'error' : success ? 'success' : 'helper';

  return (
    <div className={wrapperClasses}>
      {!floatingLabel && label && (
        <label htmlFor={inputId} className='qhs-field__label'>
          {label}
          {required && <span className='qhs-field__req' aria-hidden='true'>*</span>}
        </label>
      )}

      <div className='qhs-field__wrap'>
        {startIcon && <span className='qhs-field__icon qhs-field__icon--start'>{startIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          type={effectiveType}
          className={inputClasses}
          value={value}
          defaultValue={defaultValue}
          aria-invalid={!!error || undefined}
          aria-describedby={message ? `${inputId}-msg` : undefined}
          {...rest}
        />
        {floatingLabel && label && (
          <label htmlFor={inputId} className='qhs-field__floating-label'>
            {label}
            {required && <span className='qhs-field__req' aria-hidden='true'>*</span>}
          </label>
        )}
        {showToggle ? (
          <button
            type='button'
            className='qhs-field__toggle'
            onClick={() => setShown((s) => !s)}
            aria-label={shown ? 'Hide password' : 'Show password'}
            aria-pressed={shown}
            tabIndex={0}
          >
            {shown ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        ) : (
          endIcon && <span className='qhs-field__icon qhs-field__icon--end'>{endIcon}</span>
        )}
      </div>

      {message && (
        <span id={`${inputId}-msg`} className={`qhs-field__msg qhs-field__msg--${messageType}`}>
          {message}
        </span>
      )}
    </div>
  );
});

export default Input;
